import { useEffect } from "react";

import { filter, find, isEmpty } from "lodash/fp";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import useMeetingReportAcceptInvite from "@app/analytics/hooks/useReportAcceptInvite";
import useWorkspaceInvite from "@app/analytics/hooks/workspace/useWorkspaceInvite";
import useOnboardingStore from "@app/analytics/onboarding/hooks/useOnboardingStore";
import type {
  OnboardingStepContext,
  RouteStepId,
} from "@app/analytics/onboarding/types";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { withoutQueryParams } from "@app/common/utils";
import { parseReportInviteFromUrl } from "@app/common/utils/parsingUtils";

import { getOnboardingSource, getStepIdFromRouteStepId } from "../utils";

/**
 * This hook is responsible for initializing the onboarding state and
 * managing step state as a user moves through the onboarding flow.
 */
const useOnboarding = () => {
  const { activeStepIndex, initialize, refresh, setActiveStep, steps } =
    useOnboardingStore();

  const navigate = useNavigate();
  const location = useLocation();
  const { step: routeStepId } = useParams() as { step: RouteStepId | null };
  const [queryParams] = useSearchParams();
  const redirectTo = queryParams.get("redirectTo");
  const decodedRedirectTo = redirectTo ? decodeURIComponent(redirectTo) : null;
  const reportInviteId = parseReportInviteFromUrl(decodedRedirectTo);
  const workspaceInviteId = queryParams.get("inviteId");

  const onboardingSourceParam = getOnboardingSource(queryParams.get("source"));
  const [onboardingSource] = useLocalStorage(
    "onboardingSource",
    onboardingSourceParam,
    { scope: "session" }
  );

  // Get all currently connected sso and integration providers
  const { isLoading: isProvidersLoading } = useConnectedProviders();
  const { isLoading: isIntegrationsLoading, userIntegrations } =
    useIntegrations();
  const connectedIntegrations = filter({ isConnected: true }, userIntegrations);
  const disabledIntegrations = filter({ isDisabled: true }, userIntegrations);

  // Accept the report invite if there is one
  useMeetingReportAcceptInvite(reportInviteId, {
    signupSource: "share",
    enabled: Boolean(reportInviteId),
  });

  // determine if there is a pending workspace invite
  const { invite: workspaceInvite, isLoading: isWorkspaceInviteLoading } =
    useWorkspaceInvite(workspaceInviteId);

  // fetch feature access for the onboarding context
  const featureAccess = useFeatureAccess();

  const isLoading =
    isWorkspaceInviteLoading || isIntegrationsLoading || isProvidersLoading;

  const stepContext: OnboardingStepContext = {
    workspaceInvite,
    featureAccess,
    connectedIntegrations,
    source: onboardingSource,
    entryPoint: getStepIdFromRouteStepId(routeStepId),
  };

  useEffect(() => {
    if (isLoading) return;

    initialize(stepContext);
  }, [isLoading]);

  useEffect(() => {
    // if/when the number of disabled integrations changes, (e.g., user joined
    // a workspace that has all calendar integrations disabled), refresh the steps
    refresh(stepContext);
  }, [disabledIntegrations.length]);

  useEffect(() => {
    // navigate to the correct route when the active step index changes
    const activeStep = steps[activeStepIndex];
    if (isEmpty(activeStep)) return;
    if (!activeStep || activeStep.route === location.pathname) return;

    // when navigating to the route, strip out any query params from an
    // integration connection, so as not to pollute the new step's state
    navigate(
      withoutQueryParams(activeStep.route, location.search, [
        "action",
        "errorAction",
        "success",
        "error",
      ])
    );
  }, [steps, activeStepIndex]);

  useEffect(() => {
    if (isEmpty(steps)) return;

    // sync the active step on route changes
    const stepFromLocation = find({ route: location.pathname }, steps);
    if (!stepFromLocation) return;
    setActiveStep(stepFromLocation.id);
  }, [location.pathname]);

  return {
    isInitializing: isLoading,
  };
};

export default useOnboarding;
